import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCountriesQuery, useProvincesQuery } from '@/entities/country'
import { useDashboardStatsQuery } from '@/entities/dashboard'
import { useTenantByIdQuery } from '@/entities/tenant'
import {
  DashboardPoiChart,
  DashboardPoiChartSkeleton,
} from '@/features/dashboard/dashboard-poi-chart'
import {
  DashboardStatCards,
  DashboardStatCardsSkeleton,
} from '@/features/dashboard/dashboard-stat-cards'
import {
  DashboardStatusChart,
  DashboardStatusChartSkeleton,
} from '@/features/dashboard/dashboard-status-chart'
import { ALL_FILTER_VALUE } from '@/shared/constants/filters'
import { AdminScopeType } from '@/shared/constants/admin-scope'
import { useAdminScope } from '@/shared/hooks/use-admin-scope'
import { useScopedAdminPath } from '@/shared/hooks/use-scoped-admin-path'
import { DataTableFilterField, DataTableToolbar } from '@/shared/ui/data-table'
import { FilterPopover } from '@/shared/ui/filter-popover'
import { PageContainer } from '@/shared/ui/page-container'
import { PageHeader } from '@/shared/ui/page-header'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'

export function DashboardPage() {
  const { t } = useTranslation()
  const scopedAdminPath = useScopedAdminPath()
  const { activeTenantId = '', isSuperAdmin, scopeType } = useAdminScope()
  const currentTenantId = activeTenantId
  const [selectedCountryId, setSelectedCountryId] = useState('')
  const [selectedProvinceId, setSelectedProvinceId] = useState('')
  const [isActiveFilter, setIsActiveFilter] = useState<'all' | 'active' | 'inactive'>('all')

  const statsFilters = useMemo(
    () => ({
      ...(selectedCountryId ? { countryId: selectedCountryId } : {}),
      ...(selectedProvinceId ? { provinceId: selectedProvinceId } : {}),
      ...(isActiveFilter === 'all' ? {} : { isActive: isActiveFilter === 'active' }),
    }),
    [isActiveFilter, selectedCountryId, selectedProvinceId],
  )

  const { data: tenantResponse } = useTenantByIdQuery(currentTenantId)
  const { data: statsResponse, isLoading: isStatsLoading } = useDashboardStatsQuery(
    currentTenantId,
    statsFilters,
    scopeType,
  )
  const { data: countriesResponse } = useCountriesQuery({})
  const { data: provincesResponse } = useProvincesQuery({
    countryId: selectedCountryId,
  })

  const stats = statsResponse?.data
  const tenantName = tenantResponse?.data?.name ?? currentTenantId
  const countries = countriesResponse?.data ?? []
  const provinces = provincesResponse?.data ?? []
  const activeFilterCount = [
    selectedCountryId,
    selectedProvinceId,
    isActiveFilter !== 'all' ? isActiveFilter : '',
  ].filter(Boolean).length

  function resetAdvancedFilters() {
    setSelectedCountryId('')
    setSelectedProvinceId('')
    setIsActiveFilter('all')
  }

  return (
    <PageContainer>
      <PageHeader
        title={t('dashboard.title')}
        breadcrumbs={[
          { label: t('sidebar.tenants'), path: scopedAdminPath('/tenant') },
          ...(tenantName
            ? [{ label: tenantName, path: scopedAdminPath(`/tenant/${currentTenantId}`) }]
            : []),
          { label: t('sidebar.dashboard') },
        ]}
      />

      <div className="mb-6">
        <DataTableToolbar>
          <FilterPopover
            title={t('common.filter')}
            resetLabel={t('common.reset')}
            activeCount={activeFilterCount}
            onReset={resetAdvancedFilters}
          >
            <DataTableFilterField label={t('common.country')}>
              <Select
                value={selectedCountryId || ALL_FILTER_VALUE}
                onValueChange={(value) => {
                  const nextCountryId = value === ALL_FILTER_VALUE ? '' : value
                  setSelectedCountryId(nextCountryId)
                  setSelectedProvinceId('')
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('common.country')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_FILTER_VALUE}>{t('common.all')}</SelectItem>
                  {countries.map((country) => (
                    <SelectItem key={country.id} value={country.id}>
                      {country.nativeName ?? country.name ?? country.code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </DataTableFilterField>

            <DataTableFilterField label={t('common.province')}>
              <Select
                value={selectedProvinceId || ALL_FILTER_VALUE}
                onValueChange={(value) =>
                  setSelectedProvinceId(value === ALL_FILTER_VALUE ? '' : value)
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('common.province')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_FILTER_VALUE}>{t('common.all')}</SelectItem>
                  {provinces.map((province) => (
                    <SelectItem key={province.id} value={province.id}>
                      {province.nativeName ?? province.name ?? province.code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </DataTableFilterField>

            <DataTableFilterField label={t('common.status')}>
              <Select
                value={isActiveFilter}
                onValueChange={(value) => setIsActiveFilter(value as typeof isActiveFilter)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('common.status')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  <SelectItem value="active">{t('common.active')}</SelectItem>
                  <SelectItem value="inactive">{t('common.inactive')}</SelectItem>
                </SelectContent>
              </Select>
            </DataTableFilterField>
          </FilterPopover>
        </DataTableToolbar>
        {isStatsLoading || !stats ? (
          <DashboardStatCardsSkeleton variant={scopeType} />
        ) : (
          <DashboardStatCards stats={stats} variant={scopeType} />
        )}
      </div>

      <div className={`grid grid-cols-1 gap-4 ${isSuperAdmin ? 'lg:grid-cols-2' : ''} mb-6`}>
        {scopeType === AdminScopeType.Super && (
          <div>
            {isStatsLoading || !stats ? (
              <DashboardPoiChartSkeleton />
            ) : (
              <DashboardPoiChart data={stats.poisPerTenant} />
            )}
          </div>
        )}
        <div>
          {isStatsLoading || !stats ? (
            <DashboardStatusChartSkeleton />
          ) : (
            <DashboardStatusChart distribution={stats.poiStatusDistribution} />
          )}
        </div>
      </div>
    </PageContainer>
  )
}
